'use client'

import { CheckmarkShield12 } from '@vinted/monochrome-icons'
import { Cell, Icon, Image, Spacer, Text } from '@vinted/web-ui'
import { noop } from 'lodash'
import { Fragment } from 'react'

import {
  useConversationContext,
  useConversationSidebarContext,
} from '@marketplace-web/messaging/conversation-providers-feature'
import { FormattedRelativeTime, useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { EMPTY_USER_IMAGE_NAME, useAsset } from '@marketplace-web/shared/assets'
import {
  InboxConversationListItemDto,
  findThumbnail,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { useConversationNavigator } from '@marketplace-web/messaging/navigation-feature'
import { colorCodeToHex } from '@marketplace-web/vendor-abstractions/web-ui-util'

type Props = {
  conversation: InboxConversationListItemDto
  onClick?: () => void
}

enum ItemThumbnailSize {
  Medium = 'thumb150x210',
}

type ItemPhotoDto = {
  id: number
  thumbnails: Array<{
    type: ItemThumbnailSize | string
    url: string
  }>
}

const InboxListItem = ({ conversation, onClick = noop }: Props) => {
  const { conversation: activeConversation, getConversationRequest } = useConversationContext()
  const { closeSidebar } = useConversationSidebarContext()
  const { user } = useSession()
  const translate = useTranslate('conversations.list')
  const inboxTranslate = useTranslate('inbox')
  const asset = useAsset('assets/no-photo')
  const conversationNavigator = useConversationNavigator()

  const userId = user?.id

  const isConversationActive = conversation.id === activeConversation?.id
  const oppositeUser = conversation.opposite_user

  const { id } = conversation

  const renderPrefix = () => {
    return (
      <Image
        role="img"
        src={findThumbnail(oppositeUser?.photo) || asset(EMPTY_USER_IMAGE_NAME)}
        size="large"
        styling="circle"
      />
    )
  }

  const renderBadge = () => {
    if (oppositeUser?.badge !== 'moderator') return null

    return (
      <>
        <Spacer orientation="vertical" size="small" />
        <Icon
          name={CheckmarkShield12}
          color="primary-default"
          aria={{
            'aria-label': inboxTranslate('a11y.moderator_badge'),
          }}
          testId="moderator-badge-icon"
        />
      </>
    )
  }

  const renderUsername = () => {
    if (!oppositeUser?.login) return translate('deleted_opposite_user.login')

    return (
      <>
        {oppositeUser.login}
        {renderBadge()}
      </>
    )
  }

  const renderTitle = () => {
    return <Text as="h2" type="title" text={renderUsername()} />
  }

  const renderLastInteractionTime = () => {
    const conversationUpdatedAt = conversation.updated_at

    if (!conversationUpdatedAt) return null

    const createdAt = new Date(conversationUpdatedAt).getTime()

    return <Text as="h3" type="subtitle" text={<FormattedRelativeTime value={createdAt} />} />
  }

  const renderRemainingItemCount = (itemCount: number, itemPhotosCount: number) => {
    const remainingItemCount = itemCount - itemPhotosCount

    return remainingItemCount > 0 ? (
      <Image
        label={`+${remainingItemCount}`}
        size="medium"
        styling="rounded"
        color={colorCodeToHex('greyscale-level-3')}
      />
    ) : null
  }

  const getPhotoThumbnail = (photo: ItemPhotoDto) => {
    const thumbnail = photo?.thumbnails?.find(thumb => thumb.type === ItemThumbnailSize.Medium)

    return thumbnail?.url
  }

  const renderOrderItems = (
    itemPhotos: Array<ItemPhotoDto> | null | undefined,
    itemCount: number | null | undefined,
  ) => {
    if (!itemPhotos || !itemCount) return null

    return (
      <div>
        {itemPhotos.map(photo => (
          <Fragment key={photo.id}>
            <Image src={getPhotoThumbnail(photo)} size="medium" styling="rounded" />
            <Spacer orientation="vertical" size="small" />
          </Fragment>
        ))}
        <div data-testid="inbox-order-items" className="u-inline-block">
          {renderRemainingItemCount(itemCount, itemPhotos.length)}
        </div>
      </div>
    )
  }

  const renderMessageContent = () => {
    const { description, item_photos, item_count } = conversation

    return (
      <>
        <Text as="span" truncate text={description} testId="inbox-list-item-description" />
        <Cell
          styling="tight"
          theme="transparent"
          body={renderOrderItems(item_photos, item_count)}
        />
      </>
    )
  }

  const handleConversationClick = () => {
    closeSidebar()

    if (!userId || isConversationActive) return

    getConversationRequest({ conversationId: conversation.id })

    conversationNavigator.navigate(conversation.id)

    onClick()
  }

  return (
    <div
      className={isConversationActive ? 'u-background-gray' : ''}
      data-testid={`inbox-list-item-${id}-container`}
    >
      <Cell
        title={renderTitle()}
        prefix={renderPrefix()}
        subtitle={renderLastInteractionTime()}
        body={renderMessageContent()}
        highlighted={conversation.unread}
        type="navigating"
        theme="transparent"
        onClick={handleConversationClick}
        testId={`inbox-list-item-${id}`}
      />
    </div>
  )
}

export default InboxListItem
